import React from 'react';
import styles from './PaginationControls.module.css';

/**
 * Reusable pagination controls component
 */
interface PaginationControlsProps {
  /** Current page number (0-indexed) */
  currentPage?: number;
  /** Total number of pages */
  totalPages?: number;
  /** Total number of items */
  totalItems?: number;
  /** Items per page */
  pageSize?: number;
  /** Callback when page changes */
  onPageChange?: (page: number) => void;
  /** Whether data is loading */
  loading?: boolean;
  /** Name of items being paginated (e.g., "students", "emails") */
  itemName?: string;
  /** Additional CSS classes */
  className?: string;
}

const PaginationControls: React.FC<PaginationControlsProps> = ({
  currentPage = 0,
  totalPages = 0,
  totalItems = 0,
  pageSize = 20,
  onPageChange = () => {},
  loading = false,
  itemName = 'items',
  className = '',
}) => {
  if (totalPages <= 1) {
    return null; // Don't show pagination if only one page or no data
  }

  const startItem = currentPage * pageSize + 1;
  const endItem = Math.min((currentPage + 1) * pageSize, totalItems);

  return (
    <div className={`${styles['pagination-controls']} ${className}`}>
      <div className={styles['pagination-info']}>
        <span>
          Showing {startItem} - {endItem} of {totalItems} {itemName}
        </span>
      </div>

      <div className={styles['pagination-buttons']}>
        <button
          onClick={() => onPageChange(0)}
          disabled={currentPage === 0 || loading}
          className={styles['pagination-btn']}
          title="First page"
        >
          First
        </button>

        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 0 || loading}
          className={styles['pagination-btn']}
          title="Previous page"
        >
          Previous
        </button>

        <span className={styles['page-info']}>
          Page {currentPage + 1} of {totalPages}
        </span>

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages - 1 || loading}
          className={styles['pagination-btn']}
          title="Next page"
        >
          Next
        </button>

        <button
          onClick={() => onPageChange(totalPages - 1)}
          disabled={currentPage >= totalPages - 1 || loading}
          className={styles['pagination-btn']}
          title="Last page"
        >
          Last
        </button>
      </div>
    </div>
  );
};

export default PaginationControls;
